import { faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMantineTheme } from '@mantine/core'
import { translate } from 'i18n/i18n'
import { omit } from 'lodash'
import React from 'react'
import { captureFrontendError } from 'utils/captureFrontendError'
import type { ButtonProps, MantineButtonProps } from '../../Button'
import { ChildContent } from '../../ChildContent/ChildContent'
import { useCssOverrides } from './useCssOverrides'

export type STATE = 'default' | 'actioning' | 'done' | 'error'

export const useAsyncButtonProps = (props: ButtonProps): MantineButtonProps => {
  const minimumVisualLoadingMilliseconds = props.asyncOptions?.minimumVisualLoadingMilliseconds ?? 1000
  const successMilliseconds = props.asyncOptions?.successMilliseconds ?? 2000
  const errorMilliseconds = props.asyncOptions?.errorMilliseconds ?? 2000
  const theme = useMantineTheme()
  const [state, setState] = React.useState<STATE>('default')

  const onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setState('actioning')

    try {
      const promises = await Promise.allSettled([
        props.onClick ? props.onClick(e) : Promise.resolve(),
        new Promise((resolve) => setTimeout(resolve, minimumVisualLoadingMilliseconds)),
      ])

      promises.forEach((r) => {
        if (r.status === 'rejected') throw r.reason
      })

      setState('done')

      await new Promise((resolve) => setTimeout(resolve, successMilliseconds))
    } catch (error) {
      // A bare Promise.reject() means the caller already handled the failure and only wants the error state
      if (error !== undefined) captureFrontendError(error)

      setState('error')

      await new Promise((resolve) => setTimeout(resolve, errorMilliseconds))
    } finally {
      setState('default')
    }
  }

  const cssOverrides = useCssOverrides({ variant: props.variant, state, theme })

  // icons are put into the children for consistency and to deal with some mantine
  // styling issues; Mantine forwards unknown props to the DOM, so the app-only ones stay out
  const mantineProps = {
    ...omit(props, ['asyncOptions', 'variant', 'leftIcon', 'rightIcon', 'gap', 'async']),
    onClick,
    css: cssOverrides,
    children: <ChildContent leftIcon={props.leftIcon} content={props.children} rightIcon={props.rightIcon} />,
  }

  if (state === 'actioning') {
    return {
      ...mantineProps,
      children: (
        <ChildContent
          leftIcon={null}
          content={props.asyncOptions?.loadingContent ?? translate.phrases.banyanApp('Loading...')}
          rightIcon={props.rightIcon}
        />
      ),
      loading: true,
      loaderProps: { type: 'dots', color: 'currentColor', ...props.loaderProps },
    }
  }

  if (state === 'done') {
    return {
      ...mantineProps,
      children: (
        <ChildContent
          leftIcon={<FontAwesomeIcon icon={faCircleCheck} color={theme.colors.green[0]} />}
          content={props.asyncOptions?.successContent ?? translate.phrases.banyanApp('Done')}
          rightIcon={props.rightIcon}
        />
      ),
      disabled: true,
    }
  }

  if (state === 'error') {
    return {
      ...mantineProps,
      children: (
        <ChildContent
          leftIcon={<FontAwesomeIcon icon={faCircleXmark} color={theme.colors.red[0]} />}
          content={props.asyncOptions?.errorContent ?? translate.phrases.banyanApp('Error')}
          rightIcon={props.rightIcon}
        />
      ),
      disabled: true,
    }
  }

  return mantineProps
}
